/**
 * 同克隆对象（clonedBy.key）下，跨节点同步各分类负责人
 * 节点勾选 independentExecutors（独立负责人）时不参与同步
 */

export function getClonedByFieldKey(option = {}) {
  const clonedBy = option.clonedBy;
  if (!clonedBy) return '';
  if (typeof clonedBy === 'string') return String(clonedBy).trim();
  return String(clonedBy.key || clonedBy.fullkey || '').trim();
}

export function isIndependentExecutors(option = {}) {
  return !!option.independentExecutors;
}

function nodeOption(node) {
  return (node && (node.option || node.properties)) || {};
}

function nodeLabel(node) {
  const opt = nodeOption(node);
  return opt.name ||
    (node && node.graph && node.graph.text && node.graph.text.value) ||
    (node && node.text && node.text.value) ||
    (node && node.id) ||
    '';
}

/** BFS/列表顺序中，同克隆字段且未开独立负责人的第一个节点为组长 */
export function getCloneShareLeaderId(nodes, node) {
  if (!node) return '';
  const key = getClonedByFieldKey(nodeOption(node));
  if (!key || isIndependentExecutors(nodeOption(node))) return node.id;
  const list = Array.isArray(nodes) ? nodes : [];
  const leader = list.find((n) => {
    if (!n) return false;
    const opt = nodeOption(n);
    if (isIndependentExecutors(opt)) return false;
    return getClonedByFieldKey(opt) === key;
  });
  const preferred = list.find((n) => {
    if (!n) return false;
    const opt = nodeOption(n);
    if (isIndependentExecutors(opt)) return false;
    if (getClonedByFieldKey(opt) !== key) return false;
    const t = (n.graph && n.graph.type) || n.type || '';
    return t !== 'carbon_node';
  });
  return (preferred && preferred.id) || (leader && leader.id) || node.id;
}

export function isCloneShareFollower(nodes, node) {
  if (!node) return false;
  const opt = nodeOption(node);
  if (!getClonedByFieldKey(opt) || isIndependentExecutors(opt)) return false;
  const leaderId = getCloneShareLeaderId(nodes, node);
  return !!(leaderId && leaderId !== node.id);
}

export function getCloneShareLeader(nodes, node) {
  const leaderId = getCloneShareLeaderId(nodes, node);
  if (!leaderId) return null;
  return (Array.isArray(nodes) ? nodes : []).find((n) => n && n.id === leaderId) || null;
}

export function getCloneShareLeaderLabel(nodes, node) {
  const leader = getCloneShareLeader(nodes, node);
  return leader ? nodeLabel(leader) : '';
}

function slotKeyPart(slotId, nodeId) {
  const prefix = `${nodeId}_`;
  return slotId.startsWith(prefix) ? slotId.slice(prefix.length) : slotId;
}

function sameExecutors(a, b) {
  try {
    return JSON.stringify(a != null ? a : null) === JSON.stringify(b != null ? b : null);
  } catch (e) {
    return a === b;
  }
}

/**
 * @param {object} nodeData 当前全部节点配置
 * @param {Array} nodes 流程节点（需含 id / option）
 * @param {string} sourceNodeId 刚变更的节点
 * @param {object} sourceVal 该节点最新配置
 * @returns {object} 同步后的 nodeData
 */
export function syncCloneExecutorsAcrossNodes(nodeData, nodes, sourceNodeId, sourceVal) {
  const list = Array.isArray(nodes) ? nodes : [];
  const sourceNode = list.find((n) => n.id === sourceNodeId);
  const sourceOpt = sourceNode?.option || sourceNode?.properties || {};
  if (!getClonedByFieldKey(sourceOpt)) {
    return {...(nodeData || {}), [sourceNodeId]: sourceVal};
  }
  if (isIndependentExecutors(sourceOpt)) {
    return {...(nodeData || {}), [sourceNodeId]: sourceVal};
  }

  const fieldKey = getClonedByFieldKey(sourceOpt);
  const sourceCloned = (sourceVal && sourceVal.cloned) || {};
  const byKeyPart = {};
  Object.keys(sourceCloned).forEach((slotId) => {
    const part = slotKeyPart(slotId, sourceNodeId);
    byKeyPart[part] = sourceCloned[slotId]?.executors;
  });

  const next = {...(nodeData || {}), [sourceNodeId]: sourceVal};

  list.forEach((n) => {
    if (!n || n.id === sourceNodeId) return;
    const opt = n.option || n.properties || {};
    if (!getClonedByFieldKey(opt)) return;
    if (isIndependentExecutors(opt)) return;
    if (getClonedByFieldKey(opt) !== fieldKey) return;

    const existing = {...(next[n.id] || {})};
    const cloned = {...(existing.cloned || {})};
    let changed = false;

    Object.keys(byKeyPart).forEach((keyPart) => {
      const executors = byKeyPart[keyPart];
      if (executors === undefined) return;
      const slotId = `${n.id}_${keyPart}`;
      const prev = cloned[slotId] || {};
      const copied = Array.isArray(executors) ? [...executors] : executors;
      if (sameExecutors(prev.executors, copied)) return;
      cloned[slotId] = {
        ...prev,
        executors: copied,
      };
      changed = true;
    });

    if (changed) {
      next[n.id] = {...existing, cloned};
    }
  });

  return next;
}

function cloneDataEqual(a, b) {
  try {
    return JSON.stringify(a || {}) === JSON.stringify(b || {});
  } catch (e) {
    return a === b;
  }
}

/** 把每个共享组组长的分类负责人推到同组其它节点 */
export function syncAllSharedCloneGroups(nodeData, nodes) {
  const list = Array.isArray(nodes) ? nodes : [];
  let next = nodeData || {};
  const seen = new Set();
  list.forEach((n) => {
    if (!n) return;
    const opt = nodeOption(n);
    const key = getClonedByFieldKey(opt);
    if (!key || isIndependentExecutors(opt)) return;
    if (seen.has(key)) return;
    seen.add(key);
    const leaderId = getCloneShareLeaderId(list, n);
    const val = next[leaderId];
    if (!val) return;
    next = syncCloneExecutorsAcrossNodes(next, list, leaderId, val);
  });
  return cloneDataEqual(next, nodeData) ? (nodeData || {}) : next;
}
